<template>
  <Teleport
    v-if="isModalActive"
    :to="modalRoot"
  >
    <div
      :id="$props.id"
      ref="modalContainerRef"
      :inert="!isModalOpen"
      class="pet-modal-container"
      role="dialog"
      :aria-labelledby="titleId"
    >
      <template v-if="isModalOpen">
        <div
          :data-a11y-dialog-hide="$props.isAlert ? undefined : true"
          class="pet-modal-overlay"
        />

        <transition
          mode="out-in"
          :name="$props.animationName"
        >
          <slot />
        </transition>
      </template>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'

import A11yDialog from 'a11y-dialog'

import events$ from '@/services/Events'

import { EVENT_MODAL } from '@/config/events'

// INIT
const animationDuration = {
  timeout: 300,
  css: '300ms',
}
const emit = defineEmits(['click-primary', 'click-secondary', 'click-tertiary', 'close'])
const props = defineProps({
  id: {
    type: String,
    required: true,
  },

  isAlert: {
    type: Boolean,
    default: false,
  },

  modalRoot: {
    type: String,
    default: 'body',
  },

  animationName: {
    type: String,
    default: null,
  },
})

// DATA
const isModalActive = ref(false)
const isModalOpen = ref(false)
const isInitialOpen = ref(true)

const a11yDialog = ref()
const modalContainerRef = ref()
const fromHeight = ref(0)

// COMPUTED
const titleId = computed(() => {
  return `${props.id}-title`
})

// METHODS
function close() {
  a11yDialog.value.hide()

  modalContainerRef.value.removeAttribute('aria-hidden')
}

function disableScrolling() {
  // Store the current scroll position
  const scrollY = window.scrollY
  const scrollX = window.scrollX
  
  // Store original styles
  const bodyOverflow = document.body.style.overflow
  const bodyPosition = document.body.style.position
  const bodyTop = document.body.style.top
  const bodyLeft = document.body.style.left
  const bodyWidth = document.body.style.width
  const bodyHeight = document.body.style.height
  
  document.body.setAttribute('data-original-overflow', bodyOverflow)
  document.body.setAttribute('data-original-position', bodyPosition)
  document.body.setAttribute('data-original-top', bodyTop)
  document.body.setAttribute('data-original-left', bodyLeft)
  document.body.setAttribute('data-original-width', bodyWidth)
  document.body.setAttribute('data-original-height', bodyHeight)
  document.body.setAttribute('data-scroll-y', scrollY.toString())
  document.body.setAttribute('data-scroll-x', scrollX.toString())
  
  // Fix body positioning but ensure it maintains full viewport height
  document.body.style.position = 'fixed'
  document.body.style.top = `-${scrollY}px`
  document.body.style.left = `-${scrollX}px`
  document.body.style.width = '100%'
  document.body.style.height = `calc(100% + ${scrollY}px)`  // Ensure body extends to show footer
  document.body.style.overflow = 'hidden'
}

function enableScrolling() {
  // Get stored values
  const scrollY = document.body.getAttribute('data-scroll-y')
  const scrollX = document.body.getAttribute('data-scroll-x')
  const bodyOverflow = document.body.getAttribute('data-original-overflow')
  const bodyPosition = document.body.getAttribute('data-original-position')
  const bodyTop = document.body.getAttribute('data-original-top')
  const bodyLeft = document.body.getAttribute('data-original-left')
  const bodyWidth = document.body.getAttribute('data-original-width')
  const bodyHeight = document.body.getAttribute('data-original-height')
  
  // Restore styles
  document.body.style.position = bodyPosition || ''
  document.body.style.top = bodyTop || ''
  document.body.style.left = bodyLeft || ''
  document.body.style.width = bodyWidth || ''
  document.body.style.height = bodyHeight || ''
  document.body.style.overflow = bodyOverflow || ''
  
  // Restore scroll position
  if (scrollY !== null || scrollX !== null) {
    requestAnimationFrame(() => {
      window.scrollTo(
        scrollX ? parseInt(scrollX, 10) : 0,
        scrollY ? parseInt(scrollY, 10) : 0
      )
    })
  }
  
  // Clean up attributes
  document.body.removeAttribute('data-original-overflow')
  document.body.removeAttribute('data-original-position')
  document.body.removeAttribute('data-original-top')
  document.body.removeAttribute('data-original-left')
  document.body.removeAttribute('data-original-width')
  document.body.removeAttribute('data-original-height')
  document.body.removeAttribute('data-scroll-y')
  document.body.removeAttribute('data-scroll-x')
}

async function open() {
  isModalActive.value = true

  await nextTick()

  a11yDialog.value = new A11yDialog(modalContainerRef.value)

  a11yDialog.value.on('show', onModalShow)
  a11yDialog.value.on('hide', onModalHide)

  a11yDialog.value.show()
}

function doIt(read = false) {
  const el = modalContainerRef.value.querySelector('.pet-modal')
  const _h = getComputedStyle(el).height

  if (read) {
    return fromHeight.value || _h
  } else {
    fromHeight.value = _h
  }
}

function onModalShow() {
  disableScrolling()

  events$.emit(EVENT_MODAL.OPEN, { modalName: props.id })

  isModalOpen.value = true
}

async function onModalHide() {
  // Re-Enable background scrolling
  enableScrolling()

  // Inform listeners (e.g. dropdown) to cleanup before modal is closed
  events$.emit(EVENT_MODAL.BEFORE_CLOSING, props.id)

  // Set closing css class
  modalContainerRef.value?.classList.add('pet-modal-container--closing')

  // Await closing after event, to give listeners time to cleanup and await closing animation
  await new Promise(s => setTimeout(s, animationDuration.timeout))

  modalContainerRef.value?.classList.remove('pet-modal-container--closing')

  // Inform listeners (e.g. tracking) that modal is now closed
  events$.emit(EVENT_MODAL.CLOSE, { modalName: props.id })

  emit('close')

  isModalOpen.value = false

  await nextTick()

  isInitialOpen.value = true
  isModalActive.value = false
}

// LIFECYCLE HOOKS
onBeforeUnmount(() => {
  // Re-Enable background scrolling, e.g. when user navigates back via browser-back
  enableScrolling()

  isModalOpen.value = false
  isInitialOpen.value = true
  isModalActive.value = false
})

// EPILOGUE
defineExpose({
  id: props.id,
  open,
  close,

  isModalActive,
  isModalOpen,
  isInitialOpen,
  doIt,
})
</script>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
  }
}

@keyframes fade-out {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

@keyframes scale-in {
  from {
    opacity: 0;
    transform: scale(0);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes slide-up {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateX(0);
  }
}

.pet-modal-container,
.pet-modal-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.pet-modal-container {
  z-index: var(--pet-stack-level-progress-modal);
  isolation: isolate;
}

.pet-modal-overlay {
  background-color: var(--scrim);
  backdrop-filter: blur(2px);
}

.pet-modal-container--closing .pet-modal-overlay,
.pet-modal-container--closing .pet-modal-wrapper {
  animation-name: fade-out;
  animation-duration: v-bind('animationDuration.css');
  animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
}

.pet-modal-container:not(.pet-modal-container--closing)[inert='true'] {
  display: none;
}

.pet-modal-overlay {
  animation-name: fade-in;
  animation-duration: v-bind('animationDuration.css');
  animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);
}

.pet-modal-wrapper:not(.animation-step) {
  animation-name: scale-in;
  animation-duration: v-bind('animationDuration.css');
  animation-timing-function: cubic-bezier(0.65, 0, 0.35, 1);

  &.pet-modal-wrapper--maxi {
    animation-name: slide-up;
  }
}

.pet-modal-container :deep(.pet-modal) {
  transition: height 1s ease;
}

:deep(a) {
  color: var(--Interaction-States-enabled-default);
  border-bottom: 1px solid var(--Interaction-States-enabled-default);
  font-weight: bold;
  cursor: pointer;
}
</style>
